import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ssoApi } from '../services/api';
import { toast } from 'sonner';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const SSOCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Prevent double execution in React 18 strict mode
    if (hasProcessed) return;
    
    const handleCallback = async () => {
      setHasProcessed(true);
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      console.log('SSO Callback - code:', code ? 'present' : 'missing', 'state:', state ? state.substring(0, 20) + '...' : 'missing');

      // Check for errors from Azure AD
      if (error) {
        setStatus('error');
        setErrorMessage(errorDescription || error);
        toast.error(`SSO Error: ${errorDescription || error}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Verify we have code and state
      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Missing authorization code or state');
        toast.error('Invalid SSO callback');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Clear stored state (backend will verify it)
      sessionStorage.removeItem('sso_state');

      try {
        // Exchange code for tokens
        console.log('Calling SSO callback API...');
        const response = await ssoApi.callback(code, state);
        console.log('SSO callback response:', response.data ? 'success' : 'no data');
        const { access_token, user } = response.data;

        // Store token and user info AND update AuthContext state
        loginWithToken(access_token, user);

        setStatus('success');
        toast.success(`Welcome, ${user.name}!`);

        // Redirect to dashboard
        setTimeout(() => navigate('/'), 1000);
      } catch (error) {
        console.error('SSO callback error:', error.response?.data || error.message);
        setStatus('error');
        const message = error.response?.data?.detail || 'SSO authentication failed';
        setErrorMessage(message);
        toast.error(message);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, hasProcessed]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center p-8">
        {status === 'processing' && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
            <h2 className="text-xl font-medium text-slate-900">Completing sign in...</h2>
            <p className="text-slate-500">Please wait while we verify your credentials.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle className="w-12 h-12 text-emerald-600" />
            <h2 className="text-xl font-medium text-slate-900">Sign in successful!</h2>
            <p className="text-slate-500">Redirecting to dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="w-12 h-12 text-red-600" />
            <h2 className="text-xl font-medium text-slate-900">Sign in failed</h2>
            <p className="text-red-600">{errorMessage}</p>
            <p className="text-slate-500">Redirecting to login page...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOCallbackPage;
